import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const authAxios = axios.create({ baseURL: API, withCredentials: true });

// Silently refresh an expired access token once on 401, then retry the request.
const NO_RETRY = ["/auth/login", "/auth/register", "/auth/refresh", "/auth/logout"];
authAxios.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const skip = NO_RETRY.some((p) => (original.url || "").includes(p));
    if (error.response?.status === 401 && !original._retry && !skip) {
      original._retry = true;
      try {
        await authAxios.post("/auth/refresh");
        return authAxios(original);
      } catch {
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = checking, null = signed out, object = signed in

  useEffect(() => {
    // eslint-disable-next-line -- runs once on mount to hydrate the session
    authAxios.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  const login = async (email, password) => {
    const { data } = await authAxios.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };
  const register = async (name, email, password) => {
    const { data } = await authAxios.post("/auth/register", { name, email, password });
    setUser(data);
    return data;
  };
  const logout = async () => {
    try { await authAxios.post("/auth/logout"); } catch { /* ignore */ }
    setUser(null);
  };

  return <AuthContext.Provider value={{ user, login, register, logout }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
